import { Component, Inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, of } from 'rxjs';
import { Station, AdminStationStatus } from '../../station-configurator/station-configurator.interfaces';
import { AdminStation } from '@mol-limo-reg/admin-api';
import { StationService } from '../../../services/station.service';
import { SnackbarService } from '../../../services/snackbar.service';

export interface EditStationDialogData {
  stationId: string;
}

@Component({
  selector: 'app-edit-station-dialog',
  templateUrl: './edit-station-dialog.component.html',
  styleUrls: ['./edit-station-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditStationDialogComponent implements OnInit {
  loading = true;
  stationData?: Station;
  editForm?: FormGroup;
  AdminStationStatus = AdminStationStatus;

  constructor(
    private dialogRef: MatDialogRef<EditStationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditStationDialogData,
    private fb: FormBuilder,
    private stationService: StationService,
    private snackbarService: SnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadStationData();
  }

  private loadStationData(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.stationService
      .getStationByKey$(this.data.stationId)
      .pipe(
        catchError(() => {
          this.snackbarService.error('Az állomás adatainak lekérése során hiba lépett fel!');

          setTimeout(() => this.dialogRef.close({ success: false }), 1500);
          return of();
        }),
      )
      .subscribe((stationData) => {
        if (stationData) {
          this.stationData = stationData;
          this.initializeForm(stationData);
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private initializeForm(data: Station): void {
    this.editForm = this.fb.group({
      nameHu: [data.nameHu, Validators.required],
      nameEn: [data.nameEn, Validators.required],
      postalCode: [data.postalCode, Validators.required],
      city: [data.city, Validators.required],
      address: [data.address],
      contactPhone: [data.contactPhone, Validators.required],
      status: [data.status, Validators.required],
      isAirport: [data.isAirport, Validators.required],

      hasSurcharge: [data.availableServices?.hasSurcharge || false],
      surchargeAmount: [{ value: data.availableServices?.surchargeAmount || 0, disabled: !data.availableServices?.hasSurcharge }, [Validators.min(0)]],
      hasSnowChains: [data.availableServices?.hasSnowChains || false],
      hasBabySeat: [data.availableServices?.hasBabySeat || false],
      hasChildSeat: [data.availableServices?.hasChildSeat || false],
      hasBoosterSeat: [data.availableServices?.hasBoosterSeat || false],
      hasPhoneHolder: [data.availableServices?.hasPhoneHolder || false],
    });

    this.editForm!.get('hasSurcharge')?.valueChanges.subscribe((value) => {
      const surchargeAmountControl = this.editForm!.get('surchargeAmount');
      if (value) {
        surchargeAmountControl?.enable();
      } else {
        surchargeAmountControl?.disable();
        surchargeAmountControl?.setValue(0);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  onSave(): void {
    if (!this.editForm || !this.editForm.valid) {
      return;
    }

    const formValue = this.editForm.value;
    const stationId = this.data.stationId;

    const updateRequest: AdminStation = {
      nameHu: formValue.nameHu,
      nameEn: formValue.nameEn,
      city: formValue.city,
      address: formValue.address || '',
      postalCode: formValue.postalCode,
      contactPhone: formValue.contactPhone,
      status: formValue.status,
      isAirport: formValue.isAirport,
      availableServices: {
        hasSurcharge: formValue.hasSurcharge,
        surchargeAmount: formValue.hasSurcharge ? formValue.surchargeAmount : undefined,
        hasSnowChains: formValue.hasSnowChains,
        hasBabySeat: formValue.hasBabySeat,
        hasChildSeat: formValue.hasChildSeat,
        hasBoosterSeat: formValue.hasBoosterSeat,
        hasPhoneHolder: formValue.hasPhoneHolder,
      },
    };

    this.stationService
      .updateStationByKey$(stationId, updateRequest)
      .pipe(
        catchError(() => {
          this.snackbarService.error(`"${formValue.nameHu}" módosítása során hiba lépett fel!`);
          this.dialogRef.close({ success: false });
          return of();
        }),
      )
      .subscribe(() => {
        this.snackbarService.success(`"${formValue.nameHu}" sikeresen módosítva!`);
        this.dialogRef.close({ success: true });
      });
  }
}
